import * as React from "react";
import ModuleBody from "../../../layouts/module-body/ModuleBody";
import Icon from "../../../icon/Icon";
import DotPatternIndigoIcon from "/src/svg/dot-pattern-indigo.svg";
import LoopIcon from "/src/svg/loop.svg";
import {css} from "/src/utils/utils";
import {LoopDot} from "./loop-dot/LoopDot";
import {LoopLabel} from "./loop-label/LoopLabel";
import MobileLoopIcon from "/src/svg/mobile-loop.svg";
import {LoopCarouselCard} from "./loop-carousel-card/LoopCarouselCard";
import {MobileLoopCarousel} from "../mobile-loop-carousel/MobileLoopCarousel";
import {CarouselProps} from "../Carousel";

import * as styles from "./loop-carousel.module.scss";

const INTERVAL_DURATION_MS = 8000;

const POSITIONS = ["nodot", "top", "right", "bottom", "left"];

export function LoopCarousel(props: CarouselProps) {
  const {title, secondLineTitle, slides, autoplay} = props;
  const [rotateSlides, setRotateSlides] = React.useState<boolean>(!!autoplay);
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState<number>(0);
  const intervalID = React.useRef<null | NodeJS.Timer>(null);

  React.useEffect(() => {
    if (slides && rotateSlides && !intervalID.current) {
      intervalID.current = setInterval(() => {
        setCurrentSlideIndex(index => (index + 1) % slides.length);
      }, INTERVAL_DURATION_MS);
    }

    return () => {
      if (intervalID.current) {
        clearInterval(intervalID.current);
      }
    };
  }, [rotateSlides, slides]);

  const handleDotClick = React.useCallback(
    (index: number) => () => {
      setCurrentSlideIndex(index);
      setRotateSlides(false);
    },
    []
  );

  const handleHomeButtonClick = React.useCallback(() => {
    setCurrentSlideIndex(0);
  }, []);

  const handleSlideChange = React.useCallback((newIndex: number) => {
    setCurrentSlideIndex(newIndex);
  }, []);

  if (!slides) {
    return null;
  }

  return (
    <div className={styles.loopCarousel}>
      <ModuleBody>
        <div className={styles.loopCarousel__header}>
          <Icon
            alt="Flexport"
            fill="none"
            svg={DotPatternIndigoIcon}
            classes={styles.loopCarousel__dotPatternIcon}
          />
          <div className={styles.loopCarousel__header__title}>
            <h2 className="h2">
              {title}
              <br />
              {secondLineTitle}
            </h2>
          </div>
        </div>
        <div className={styles.loopCarousel__body}>
          <div className={styles.loopCarousel__imgColumn}>
            <div className={styles.loopCarousel__imgColumn__img}>
              <Icon
                fill="none"
                svg={LoopIcon}
                classes={styles.loopCarousel__loopIcon}
                alt="Flexport"
              />
            </div>
            <div
              className={css(
                styles.loopCarousel__dashedCircle,
                styles.loopCarousel__dashedCircle__1
              )}
            />
            <div
              className={css(
                styles.loopCarousel__dashedCircle,
                styles.loopCarousel__dashedCircle__2
              )}
            />
            <div
              className={css(
                styles.loopCarousel__dashedCircle,
                styles.loopCarousel__dashedCircle__3
              )}
            />
            <div
              className={css(
                styles.loopCarousel__dashedCircle,
                styles.loopCarousel__dashedCircle__4
              )}
            />

            {slides
              ?.map(
                (slide, index) =>
                  slide && (
                    <LoopLabel
                      position={POSITIONS[index] || POSITIONS[0]}
                      highlightedIcon={slide.highlightedIcon}
                      timelineButtonText={slide.timelineButtonText}
                      key={index}
                      isActive={index === currentSlideIndex}
                    />
                  )
              )
              .slice(1)}

            {slides
              .map((_, index) => (
                <LoopDot
                  key={index}
                  isActive={
                    index === currentSlideIndex ||
                    (currentSlideIndex === 0 && index === 1)
                  }
                  position={POSITIONS[index] || POSITIONS[0]}
                  onClick={handleDotClick(index)}
                />
              ))
              .slice(1)}

            <button
              type="button"
              className={styles.loopCarousel__homeButton}
              onClick={handleHomeButtonClick}
            />
          </div>
          {slides.map(
            (slide, index) =>
              slide && (
                <LoopCarouselCard
                  key={index}
                  slide={slide}
                  isActive={index === currentSlideIndex}
                />
              )
          )}
        </div>
        <div className={styles.loopCarousel__mobileBody}>
          <Icon
            classes={styles.loopCarousel__mobileBody__icon}
            fill="none"
            svg={MobileLoopIcon}
          />
          <MobileLoopCarousel
            slides={slides}
            onCurrentSlideChange={handleSlideChange}
          >
            {(slide, index) =>
              slide && <LoopCarouselCard key={index} slide={slide} isActive />
            }
          </MobileLoopCarousel>
        </div>
      </ModuleBody>
    </div>
  );
}
